import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CircleUser, ExternalLink, Layers } from 'lucide-react';
import useStore from '../store/useStore';
import { useReactFlow } from 'reactflow'; // Added Edge
import { v4 as uuidv4 } from 'uuid';
import { shallow } from "zustand/shallow";
import ReactMarkdown from 'react-markdown';
// Import DestinationNodeData specifically
import { CustomNode, AnyNodeData, Node, CustomEdge, DestinationNodeData } from "../types";
import { getEduBoxContentClass, getEduBoxEmptyClass } from "../styles/eduBox";
import { nodeDocumentation } from "../node-types";
import ProjectConfigRenderer from './project/config/ProjectConfigRenderer';
import EdgeConfigRenderer from './edge/config/EdgeConfigRenderer'; // Import EdgeConfigRenderer

// Define modes
type CanvasMode = 'project' | 'playbook';

interface RightPanelProps {
  isEmbedded?: boolean;
  selectedNode?: Node<AnyNodeData> | CustomNode | null;
  selectedEdge?: CustomEdge | null; // Added selectedEdge prop
  onClose?: () => void;
  mode?: CanvasMode;
}

// Create a global portal element ID
const AUTOCOMPLETE_PORTAL_ID = 'autocomplete-portal-root';

function RightPanel({ isEmbedded = false, selectedNode, selectedEdge, mode = 'project' }: RightPanelProps) { // Added selectedEdge
  const { getNodes, setNodes } = useReactFlow(); // Added getEdges
  const {
    takeSnapshot,
    isRightPanelManuallyCollapsed, // Get state from store
    togglePanels, // Get action from store
  } = useStore(
    (state) => ({
      takeSnapshot: state.takeSnapshot,
      isRightPanelManuallyCollapsed: state.isRightPanelManuallyCollapsed,
      togglePanels: state.togglePanels,
    }),
    shallow,
  );

  // Function to create a group node containing the selected nodes
  const createGroupFromSelection = useCallback(() => {
    // Take a snapshot for undo/redo
    takeSnapshot();

    const allNodes = getNodes();
    const allSelectedNodes = allNodes.filter(node => node.selected);
    if (allSelectedNodes.length <= 1) return; // Need at least two nodes/groups to form a new group

    const allSelectedNodeIds = new Set(allSelectedNodes.map(n => n.id));

    // Identify top-level selected nodes (nodes whose parent is not also selected)
    const topLevelSelectedNodes = allSelectedNodes.filter(node =>
      !node.parentId || !allSelectedNodeIds.has(node.parentId)
    );

    // If only one top-level node/group is selected (e.g., a group and its children), don't create a new group
    if (topLevelSelectedNodes.length <= 1) return;

    // Calculate the bounding box based *only* on the top-level selected nodes
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    topLevelSelectedNodes.forEach(node => {
      const x = node.position.x;
      const y = node.position.y;
      const width = node.width || 150; // Default width if not specified
      const height = node.height || 40; // Default height if not specified
      
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    // Add some padding
    const padding = 20;
    const headerOffset = 24; // Extra space for group header
    
    minX -= padding;
    minY -= padding + headerOffset; // Add extra space at the top
    maxX += padding;
    maxY += padding;
    
    // Create the group node
    const groupId = `group-${uuidv4()}`;
    const groupNode = {
      id: groupId,
      type: 'group',
      position: { x: minX, y: minY },
      style: { width: maxX - minX, height: maxY - minY },
      data: {
        // Use topLevelSelectedNodes.length for the name
        name: `Group ${topLevelSelectedNodes.length} items`,
        color: undefined, // Set color to undefined for default styling
        icon: 'layers',
        customLabel: '',
        groupNameVisibility: 'visible',
        iconVisibility: 'visible',
        customLabelVisibility: 'visible',
        allowedNodeTypes: [],
        // Use topLevelSelectedNodes.length for the label
        label: `Group ${topLevelSelectedNodes.length} items`,
        type: 'group',
        project: '',
        handles: []
      }
    };

    // Get IDs of only the top-level selected nodes that will be re-parented
    const topLevelSelectedNodeIds = new Set(topLevelSelectedNodes.map(node => node.id));

    // Create the new list of nodes
    const nextNodes = allNodes.map(node => {
      // Only modify the top-level selected nodes
      if (topLevelSelectedNodeIds.has(node.id)) {
        return {
          ...node,
          parentId: groupId, // Assign to the new group
          position: {
            x: node.position.x - minX,
             y: node.position.y - minY
           },
           extent: 'parent' as const, // Explicitly cast to literal type
           selected: false // Deselect nodes after grouping
         };
      }
      return node; // Keep non-selected nodes as they are
    });

    // Add the group node and the updated nodes
    setNodes([groupNode, ...nextNodes]);

    // Optional: Reset selection state if needed (similar to the example)
    // useStoreApi().getState().resetSelectedElements();
    // useStoreApi().setState({ nodesSelectionActive: false });

  }, [getNodes, setNodes, takeSnapshot]); // Removed addNode as it's not directly used here
  const [width, setWidth] = useState(isEmbedded ? 252 : 360);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [isConfigCollapsed, setIsConfigCollapsed] = useState(() => {
    if (!isEmbedded) {
      const savedConfigState = localStorage.getItem('configBoxCollapsed');
      return savedConfigState === 'true';
    }
    return false;
  });
  const [isEduCollapsed, setIsEduCollapsed] = useState(() => {
    if (isEmbedded) return false;
    const savedEduState = localStorage.getItem('eduBoxCollapsed');
    return savedEduState === 'true';
  });
  const eduBoxRef = useRef<HTMLDivElement>(null);
  const configBoxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prevSelectedNodeRef = useRef<Node<AnyNodeData> | CustomNode | null | undefined>(null);
  const prevSelectedEdgeRef = useRef<CustomEdge | null | undefined>(null); // Added ref for edge
  const dragControls = useDragControls();
  const isNodeDragging = useStore(state => state.isNodeDragging, shallow);
  const [panelHeight, setPanelHeight] = useState(0);
  const [, setConfigBoxHeight] = useState(0);
  const [eduBoxAvailableHeight, setEduBoxAvailableHeight] = useState(0);
  const configBoxContentRef = useRef<HTMLDivElement>(null);
  const eduBoxContentRef = useRef<HTMLDivElement>(null);
  const [highlightConfigHeader, setHighlightConfigHeader] = useState(false);
  const [highlightEduHeader, setHighlightEduHeader] = useState(false);

  const getPanelTopClass = () => isEmbedded ? 'top-0' : 'top-14';
  const getPanelHeightClass = () => isEmbedded ? 'h-screen' : 'h-[calc(100vh-56px)]';

  // Determine if any element (node or edge) is selected
  const isElementSelected = !!selectedNode || !!selectedEdge;

  const getConfigBoxMaxHeight = () => {
    if (isConfigCollapsed) return 'max-h-0'; // Collapsed takes precedence
    if (!isElementSelected) return 'max-h-[48px]'; // No selection, minimal height for placeholder
    // If an element is selected and both boxes are open, split the space (adjust ratio as needed)
    if (!isConfigCollapsed && !isEduCollapsed && isElementSelected) {
      return 'overflow-y-auto min-h-[48px] max-h-[calc((100vh-56px)*2/3)]';
    }
    // If an element is selected and only config is open, give it most space
    return 'overflow-y-auto min-h-[48px] max-h-[calc(100vh-56px-48px)]'; // Subtract EduBox header height
  };

  useLayoutEffect(() => {
    if (!panelRef.current) return;
    const updateHeight = () => {
      if (panelRef.current) setPanelHeight(panelRef.current.clientHeight);
      if (configBoxRef.current) setConfigBoxHeight(configBoxRef.current.clientHeight);
      if (panelRef.current && configBoxRef.current) {
        const panelClientHeight = panelRef.current.clientHeight;
        const configBoxClientHeight = isConfigCollapsed ? 36 : configBoxRef.current.clientHeight;
        const eduBoxHeaderHeight = isEmbedded ? 0 : 36;
        const availableHeight = panelClientHeight - configBoxClientHeight - eduBoxHeaderHeight - 16;
        setEduBoxAvailableHeight(Math.max(availableHeight, 100));
      }
    };
    updateHeight();
    const panelResizeObserver = new ResizeObserver(updateHeight);
    panelResizeObserver.observe(panelRef.current);
    if (configBoxRef.current) panelResizeObserver.observe(configBoxRef.current);
    if (configBoxContentRef.current) panelResizeObserver.observe(configBoxContentRef.current);
    return () => panelResizeObserver.disconnect();
  }, [isConfigCollapsed, selectedNode, selectedEdge, isEmbedded]); // Added selectedEdge dependency

  useEffect(() => {
    if (isEmbedded) return;
    if (panelHeight < 680 && !isConfigCollapsed && !isEduCollapsed) {
      setIsEduCollapsed(true);
      localStorage.setItem('eduBoxCollapsed', 'true');
    }
  }, [panelHeight, isConfigCollapsed, isEduCollapsed, isEmbedded]);

  // Collapse panel if nothing is selected in embedded mode
  useEffect(() => {
    if (isEmbedded) setIsCollapsed(!selectedNode && !selectedEdge);
  }, [selectedNode, selectedEdge, isEmbedded]);

  // Handle auto-expanding/highlighting when selection changes
  useEffect(() => {
    const currentSelectedId = selectedNode?.id || selectedEdge?.id;
    const prevSelectedId = prevSelectedNodeRef.current?.id || prevSelectedEdgeRef.current?.id;

    // Auto-expand config box if collapsed and a new element is selected (and not dragging)
    if (currentSelectedId && isConfigCollapsed && !isNodeDragging && !prevSelectedId) {
       setIsConfigCollapsed(false);
       if (!isEmbedded) localStorage.setItem('configBoxCollapsed', 'false');
    }

    // Highlight headers if collapsed and selection changes to a *different* element
    if (currentSelectedId && currentSelectedId !== prevSelectedId) {
      if (isConfigCollapsed && !isEmbedded) {
        setHighlightConfigHeader(true);
        setTimeout(() => setHighlightConfigHeader(false), 1500);
      }
      // Only highlight EduBox if a NODE is selected (or decide if edge selection should highlight it too)
      if (selectedNode && isEduCollapsed && !isEmbedded) {
        setHighlightEduHeader(true);
        setTimeout(() => setHighlightEduHeader(false), 1500);
      }
    }

    // Update previous selection refs
    prevSelectedNodeRef.current = selectedNode;
    prevSelectedEdgeRef.current = selectedEdge;

  }, [selectedNode, selectedEdge, isConfigCollapsed, isEduCollapsed, isNodeDragging, isEmbedded]);

  const toggleConfigCollapse = () => {
    if (isEmbedded) return;
    const newState = !isConfigCollapsed;
    setIsConfigCollapsed(newState);
    if (!newState && panelHeight < 680 && !isEduCollapsed) {
      setIsEduCollapsed(true);
      localStorage.setItem('eduBoxCollapsed', 'true');
    }
    localStorage.setItem('configBoxCollapsed', String(newState));
  };

  const toggleEduCollapse = () => {
    if (isEmbedded) return;
    const newState = !isEduCollapsed;
    setIsEduCollapsed(newState);
    if (!newState && panelHeight < 680 && !isConfigCollapsed) {
      setIsConfigCollapsed(true);
      localStorage.setItem('configBoxCollapsed', 'true');
    }
    localStorage.setItem('eduBoxCollapsed', String(newState));
  };

  const variants = {
    expanded: { opacity: 1, x: 0, pointerEvents: 'auto' as const },
    collapsed: { opacity: 0, x: width + 100, pointerEvents: 'none' as const },
    dragging: { opacity: 0.5, x: 0, pointerEvents: 'none' as const },
    manualCollapsed: { opacity: 1, x: width, pointerEvents: 'auto' as const }
  };

  const contentVariants = {
    open: { opacity: 1, height: "auto", overflow: "visible", marginBottom: "8px", transition: { height: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] }, opacity: { duration: 0.15, delay: 0.05 } } },
    closed: { opacity: 0, height: 0, overflow: "hidden", marginBottom: 0, transition: { height: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] }, opacity: { duration: 0.1 } } }
  };

  const eduBoxVariants = {
    open: { opacity: 1, height: "auto", overflow: "visible", flex: 3, transition: { height: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] }, opacity: { duration: 0.15, delay: 0.05 } } },
    closed: { opacity: isEmbedded ? 1 : 0, height: isEmbedded ? "auto" : 0, overflow: isEmbedded ? "visible" : "hidden", marginTop: isEmbedded ? "8px" : 0, marginBottom: isEmbedded ? "8px" : 0, flex: isEmbedded ? 1 : 0, transition: { height: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] }, opacity: { duration: 0.1 } } }
  };

  const getEmbeddedClass = () => isEmbedded ? 'scale-90 origin-top' : '';

  useEffect(() => {
    let portalRoot = document.getElementById(AUTOCOMPLETE_PORTAL_ID);
    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = AUTOCOMPLETE_PORTAL_ID;

      Object.assign(portalRoot.style, { position: 'fixed', top: '0', left: '0', width: '100%', height: '0', zIndex: '9999' });
      document.body.appendChild(portalRoot);
    }
    return () => {
      if (portalRoot && portalRoot.childNodes.length === 0 && document.body.contains(portalRoot)) {
        document.body.removeChild(portalRoot);
      }
    };
  }, []);

  const getPortalRoot = () => document.getElementById(AUTOCOMPLETE_PORTAL_ID);

  return (
    <motion.div
      ref={panelRef}
      className={`fixed ${getPanelTopClass()} right-0 ${getPanelHeightClass()} bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-10 ${getEmbeddedClass()}`}
      initial="expanded"
      animate={isRightPanelManuallyCollapsed ? "manualCollapsed" : isCollapsed ? "collapsed" : isNodeDragging ? "dragging" : "expanded"} // Use store state
      variants={variants}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ width: `${width}px` }}
    >
      {/* ConfigBox */}
      {!isEmbedded && (
        <motion.div
          ref={configBoxRef}
          className={`flex flex-col ${isConfigCollapsed ? 'flex-none' : 'flex-none'} overflow-hidden config-section`}
        >
          <div
            className={`w-full bg-primary-100 hover:bg-primary-200 dark:bg-gray-700 hover:dark:bg-gray-600 h-9 px-4 flex justify-between items-center cursor-pointer flex-shrink-0 transition-colors duration-300 ${highlightConfigHeader ? 'bg-blue-500/30 dark:bg-blue-600/10' : ''}`}
            onClick={toggleConfigCollapse}
          >
            <span className="text-blue-900 dark:text-white font-semibold text-sm">Settings</span>
            {isConfigCollapsed ? <ChevronDown className="text-blue-900 dark:text-white" size={16} /> : <ChevronUp className="text-blue-900 dark:text-white" size={16} />}
          </div>
          <motion.div
            className="overflow-hidden"
            initial={isConfigCollapsed ? "closed" : "open"}
            animate={isConfigCollapsed ? "closed" : "open"}
            variants={contentVariants}
          >
            <div ref={configBoxContentRef} className={`px-4 pt-3 pb-4 ${getConfigBoxMaxHeight()}`}> {/* Adjusted padding */}
              {(() => {
                const selectedNodesCount = getNodes().filter(n => n.selected).length;

                if (selectedNodesCount > 1) {
                  // --- Grouping Logic ---
                  return (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm font-medium">
                      <button
                        onClick={createGroupFromSelection}
                        className="flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                      >
                        <Layers className="mr-2" size={16} />
                        Group selected nodes ({selectedNodesCount})
                      </button>
                    </div>
                  );
                } else if (selectedEdge) {
                   // --- Edge Config ---
                   return <EdgeConfigRenderer edge={selectedEdge} />;
                } else if (selectedNode) {
                  // --- Node Config ---
                  return (
                    <>
                      {(
                        <ProjectConfigRenderer
                          node={selectedNode as CustomNode}
                          isEmbedded={isEmbedded}
                          getPortalRoot={getPortalRoot}
                        />
                      )}
                    </>
                  );
                } else {
                  // --- No Selection ---
                  return (
                    <div className="flex flex-col pt-2 items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">
                      Select a node or edge to configure
                    </div>
                  );
                }
              })()}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* EduBox */}
      <motion.div className={`flex flex-col ${isEduCollapsed ? 'flex-none' : 'flex-[3]'} overflow-hidden`}>
        {!isEmbedded && (
          <div
            className={`w-full mt-0.5 bg-primary-100 hover:bg-primary-200 dark:bg-gray-700 hover:dark:bg-gray-600 h-9 px-4 flex justify-between items-center cursor-pointer flex-shrink-0 transition-colors duration-300 ${highlightEduHeader ? 'bg-blue-500/30 dark:bg-blue-500/10' : ''}`}
            onClick={toggleEduCollapse}
          >
            <span className="text-blue-900 dark:text-white font-semibold text-sm">Learn More</span>
            {isEduCollapsed ? <ChevronDown className="text-blue-900 dark:text-white" size={16} /> : <ChevronUp className="text-blue-900 dark:text-white" size={16} />}
          </div>
        )}
        <motion.div
          ref={eduBoxRef}
          className="flex flex-col origin-bottom"
          initial="open"
          animate={isEmbedded ? "open" : (isEduCollapsed ? "closed" : "open")}
          variants={eduBoxVariants}
        >
          <div
            ref={eduBoxContentRef}
            className={`overflow-y-auto px-2 pb-8 ${isEmbedded ? 'h-[calc(100vh)]' : ''}`}
            style={{ height: isEmbedded ? '100vh' : isEduCollapsed ? '0px' : `${eduBoxAvailableHeight}px` }}
          >
            <div className={`${getEduBoxContentClass()} flex flex-col justify-between`}>
              {/* EduBox currently only shows content for selected NODES */}
              {selectedNode ? (
                mode === 'project' && selectedNode.type === 'destination' ? (
                  (() => {
                    // Type assertion for destination node data
                    const destData = selectedNode.data as DestinationNodeData;
                    // Check if it's actually marked as a destination (assuming a boolean flag exists)
                    // If not, maybe show a generic message or nothing?
                    // Example check (adjust property name if needed):
                    // if (destData.isDestination === false) return <div className="text-gray-500 dark:text-gray-400 italic">This integration is not configured as a destination.</div>;

                    let description = destData.description || (typeof destData.docs === 'string' ? destData.docs : '');
                    if (!description) return <div className="text-gray-500 dark:text-gray-400 italic">Select a destination to view its documentation</div>;

                    return (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 pb-4">
                          {destData.icon && <div className="w-12 h-12"><img src={destData.icon} alt={`${destData.name} Logo`} className="object-contain" /></div>}
                          <div className="flex-1 pt-3">
                            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{destData.category}</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-gray-50">{destData.name}</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800"><CircleUser className="mr-1 h-3.5 w-3.5" />Supports Sync Injector</span>
                        </div>
                        <div className="mb-2 gap-2">
                          {destData.docs && (
                            <>
                              {typeof destData.docs === 'string' ? <a href={destData.docs} className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium inline-flex items-center" target="_blank" rel="noopener noreferrer">Documentation<ExternalLink className="ml-1 h-3.5 w-3.5" /></a>
                                : Array.isArray(destData.docs) ? destData.docs.map((docItem: string | { label?: string; url?: string }, index: number) => { // Added types for docItem and index
                                  const label = typeof docItem === 'object' && docItem.label ? docItem.label : `Documentation ${index + 1}`;
                                  const url = typeof docItem === 'object' && docItem.url ? docItem.url : typeof docItem === 'string' ? docItem : undefined;
                                  return url ? <a key={index} href={url} className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium inline-flex items-center mb-1" target="_blank" rel="noopener noreferrer">{label}<ExternalLink className="ml-1 h-3.5 w-3.5" /></a> : null;
                                })
                                : destData.docs && typeof destData.docs === 'object' && 'links' in destData.docs ? (destData.docs as { links: Array<{ label: string, url: string }> }).links.map((link: { label: string, url: string }, index: number) => <a key={index} href={link.url} className="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium inline-flex items-center mb-1" target="_blank" rel="noopener noreferrer">{link.label || `Documentation ${index + 1}`}<ExternalLink className="ml-1 h-3.5 w-3.5" /></a>) // Added types for link and index
                                : null}
                            </>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">{description}</div>
                        <div className="space-y-2">
                          <h2 className="text-base font-medium text-gray-800 dark:text-gray-100">Benefits of Using {destData.name} with MetaRouter</h2>
                          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                            <li className="flex items-start"><span className="text-green-500 mr-2">•</span><span>Up to 30% more data into {destData.name} compared to other third-party tags & streaming platforms</span></li>
                            <li className="flex items-start"><span className="text-green-500 mr-2">•</span><span>Server-side event stream that enables real-time journeys</span></li>
                            <li className="flex items-start"><span className="text-green-500 mr-2">•</span><span>First-party, parameter-by-parameter control over data sent to {destData.name}</span></li>
                            <li className="flex items-start"><span className="text-green-500 mr-2">•</span><span>No {destData.name} or other third-party tags required</span></li>
                          </ul>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3 border-l-4">
                          <p className="text-sm"><span className="font-medium text-green-800 dark:text-green-200">Est. Page load-time savings:</span> {destData.latencyImpact || '0'} ms</p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  selectedNode.type && nodeDocumentation[selectedNode.type as keyof typeof nodeDocumentation] ? <ReactMarkdown>{nodeDocumentation[selectedNode.type as keyof typeof nodeDocumentation]}</ReactMarkdown>
                  : selectedNode.data?.documentation ? <ReactMarkdown>{selectedNode.data.documentation}</ReactMarkdown>
                  : <div className={getEduBoxEmptyClass()}>Documentation for '{selectedNode.type}' nodes coming soon</div>
                )
              ) : (
                // Show default message if no node is selected (even if an edge is)
                <ReactMarkdown>{isEmbedded ? nodeDocumentation['empty-canvas-embedded'] : nodeDocumentation['empty-canvas']}</ReactMarkdown>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Resize handle */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-2 group hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors duration-150"
        style={{ cursor: 'default' }}
        onPanStart={(e, _) => dragControls.start(e)}
        onPan={(_, info) => {
          const newWidth = Math.max(isEmbedded ? 252 : 360, Math.min(600, width - info.delta.x));
          setWidth(newWidth);
        }}
        onMouseEnter={(e) => { e.currentTarget.style.cursor = 'col-resize'; }}
        onMouseLeave={(e) => { e.currentTarget.style.cursor = 'default'; }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-400 dark:group-hover:bg-blue-500 transition-colors duration-150" />
      </motion.div>

      {/* Manual Collapse/Expand Button */}
      <motion.button
        className="absolute left-[-26px] top-[8%] z-20 p-1 bg-white dark:bg-gray-700 border-t border-l border-b border-gray-300 dark:border-gray-900 rounded-l-md shadow-md hover:bg-gray-100 dark:hover:bg-gray-600"
        onClick={togglePanels} // Use togglePanels from store
        title={isRightPanelManuallyCollapsed ? "Expand Panel" : "Collapse Panel"} // Use store state
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {isRightPanelManuallyCollapsed ? <ChevronLeft size={16} className="text-gray-600 dark:text-gray-300" /> : <ChevronRight size={16} className="text-gray-600 dark:text-gray-300" />} {/* Use store state */}
      </motion.button>
    </motion.div>
  );
}

export default RightPanel;
